import { useCallback, useState } from 'react';
import { REGIONS, SECTOR_TAGS, STAGES, type SectorTag, type Stage } from '@nemo/core/model';
import type { StageMode } from '../ui/FilterDialog.js';
import {
  ALL_CANDIDATE_TYPES,
  CANDIDATE_SORT_OPTIONS,
  CONTACT_OPTIONS,
  KNOW_ANYONE_OPTIONS,
  SOURCE_OPTIONS,
  type CandidateTypeFilter,
} from './format.js';
import type {
  CandidateRegionFilter,
  CandidateSort,
  CandidateSourceFilter,
  ContactFilter,
  KnowAnyoneFilter,
} from './types.js';

/**
 * The candidates page's filters, persisted the way the grid keeps its column
 * order: per browser, best effort, never a thing a read or a write may fail
 * over.
 */

const STORAGE_KEY = 'nemo.candidates.filters';

/** The Region filter's shorthand for the six European regions, as the api spells it. */
export const EUROPE_REGION_FILTER = 'Europe' satisfies CandidateRegionFilter;

/** Every region value the api takes, "Europe" first, in the order a picker lists them. */
export const CANDIDATE_REGION_FILTERS: readonly CandidateRegionFilter[] = [
  EUROPE_REGION_FILTER,
  ...REGIONS,
];

export interface CandidateFilters {
  minRounds: number;
  types: readonly CandidateTypeFilter[];
  /** What was typed in the search box, over the name and the companies backed. */
  text: string;
  /** ISO codes; a candidate based in any one of them is kept. Empty is no filter. */
  hqCountries: readonly string[];
  /** Europe only, the feed's own country set; combined with `hqCountries`, both hold. */
  europeOnly: boolean;
  /** The list's order; the api's default is `overlap`. */
  sort: CandidateSort;
  /** Only names that have sat on a round beside a fund on our list, or in our portfolio. */
  coInvestedOnly: boolean;
  /** Whether `stages` asks what it invests at or what it leads at, as on the funds grid. */
  stageMode: StageMode;
  stages: readonly Stage[];
  /** Sectors of the companies in its rounds. */
  sectors: readonly SectorTag[];
  /** Regions of the companies in its rounds, not where it is based. */
  regions: readonly CandidateRegionFilter[];
  /** What the Affinity check said; empty is no filter. */
  knowAnyone: readonly KnowAnyoneFilter[];
  /** How recent the last contact is; empty is no filter. */
  contact: readonly ContactFilter[];
  /** Where it came from; empty is no filter. */
  sources: readonly CandidateSourceFilter[];
}

const KNOW_ANYONE_VALUES: readonly KnowAnyoneFilter[] = KNOW_ANYONE_OPTIONS.map(
  (option) => option.id,
);
const CONTACT_VALUES: readonly ContactFilter[] = CONTACT_OPTIONS.map((option) => option.id);
const SOURCE_VALUES: readonly CandidateSourceFilter[] = SOURCE_OPTIONS.map((option) => option.id);

/**
 * Production carries 5,439 pending candidates because every investor in every
 * European round becomes one. These two defaults match the api's own
 * (`DEFAULT_CANDIDATE_MIN_ROUNDS`, `DEFAULT_CANDIDATE_TYPES`), so a person who
 * has never touched the controls sees the same list the api would default to
 * on its own.
 */
export const DEFAULT_CANDIDATE_FILTERS: CandidateFilters = {
  minRounds: 2,
  types: ['vc', 'corporate_vc', 'family_office', 'accelerator'],
  text: '',
  hqCountries: [],
  europeOnly: false,
  sort: 'overlap',
  coInvestedOnly: false,
  stageMode: 'invests',
  stages: [],
  sectors: [],
  regions: [],
  knowAnyone: [],
  contact: [],
  sources: [],
};

function isCandidateSort(value: unknown): value is CandidateSort {
  return CANDIDATE_SORT_OPTIONS.some((option) => option.id === value);
}

/** The values of `vocabulary` a stored array names, in the vocabulary's order. */
function storedOf<T extends string>(
  parsed: object,
  key: string,
  vocabulary: readonly T[],
): T[] | null {
  const value: unknown = Reflect.get(parsed, key);
  if (!Array.isArray(value)) return null;
  return vocabulary.filter((entry) => value.includes(entry));
}

function read(): CandidateFilters {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === null) return DEFAULT_CANDIDATE_FILTERS;
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_CANDIDATE_FILTERS;
    const minRounds =
      'minRounds' in parsed && typeof parsed.minRounds === 'number' && parsed.minRounds >= 0
        ? parsed.minRounds
        : DEFAULT_CANDIDATE_FILTERS.minRounds;
    const types = typesOf(parsed);
    const text = 'text' in parsed && typeof parsed.text === 'string' ? parsed.text : '';
    const hqCountries =
      'hqCountries' in parsed && Array.isArray(parsed.hqCountries)
        ? parsed.hqCountries.filter(
            (code): code is string => typeof code === 'string' && /^[A-Z]{2}$/.test(code),
          )
        : [];
    const europeOnly = 'europeOnly' in parsed && parsed.europeOnly === true;
    const sort =
      'sort' in parsed && isCandidateSort(parsed.sort)
        ? parsed.sort
        : DEFAULT_CANDIDATE_FILTERS.sort;
    const coInvestedOnly = 'coInvestedOnly' in parsed && parsed.coInvestedOnly === true;
    const stageMode = 'stageMode' in parsed && parsed.stageMode === 'leads' ? 'leads' : 'invests';
    return {
      minRounds,
      types: types.length === 0 ? DEFAULT_CANDIDATE_FILTERS.types : types,
      text,
      hqCountries,
      europeOnly,
      sort,
      coInvestedOnly,
      stageMode,
      stages: storedOf(parsed, 'stages', STAGES) ?? [],
      sectors: storedOf(parsed, 'sectors', SECTOR_TAGS) ?? [],
      regions: storedOf(parsed, 'regions', CANDIDATE_REGION_FILTERS) ?? [],
      knowAnyone: storedOf(parsed, 'knowAnyone', KNOW_ANYONE_VALUES) ?? [],
      contact: storedOf(parsed, 'contact', CONTACT_VALUES) ?? [],
      sources: storedOf(parsed, 'sources', SOURCE_VALUES) ?? [],
    };
  } catch {
    // A private window or a cleared store is simply no preference.
    return DEFAULT_CANDIDATE_FILTERS;
  }
}

/**
 * Stored types from before Dealroom's `corporate` was split (no
 * `typesVersion`) meant the whole bucket, so `corporate` brings
 * `corporate_vc` along; and the old default itself (VC, corporate, family
 * office: never chosen, only kept) becomes today's default.
 */
const TYPES_VERSION = 2;
const OLD_DEFAULT_TYPES: readonly string[] = ['vc', 'corporate', 'family_office'];

function typesOf(parsed: object): CandidateTypeFilter[] {
  const types = storedOf(parsed, 'types', ALL_CANDIDATE_TYPES) ?? [];
  if (Reflect.get(parsed, 'typesVersion') === TYPES_VERSION) return types;
  if (
    types.length === OLD_DEFAULT_TYPES.length &&
    OLD_DEFAULT_TYPES.every((type) => types.some((kept) => kept === type))
  )
    return [...DEFAULT_CANDIDATE_FILTERS.types];
  return types.includes('corporate') && !types.includes('corporate_vc')
    ? ALL_CANDIDATE_TYPES.filter((type) => type === 'corporate_vc' || types.includes(type))
    : types;
}

function write(filters: CandidateFilters): void {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...filters, typesVersion: TYPES_VERSION }),
    );
  } catch {
    // Nothing here is worth failing an edit over.
  }
}

export interface CandidateFiltersControl {
  filters: CandidateFilters;
  /**
   * Every change goes through here. An empty type selection is never kept:
   * nothing checked would read as "no filter" server-side and quietly widen
   * back to everything, so it falls back to the defaults.
   */
  setFilters: (update: (filters: CandidateFilters) => CandidateFilters) => void;
}

export function useCandidateFilters(): CandidateFiltersControl {
  const [filters, setState] = useState<CandidateFilters>(() => read());

  const setFilters = useCallback((update: (filters: CandidateFilters) => CandidateFilters) => {
    setState((current) => {
      const next = update(current);
      const kept: CandidateFilters = {
        ...next,
        minRounds: Math.max(0, next.minRounds),
        types: next.types.length === 0 ? DEFAULT_CANDIDATE_FILTERS.types : next.types,
      };
      write(kept);
      return kept;
    });
  }, []);

  return { filters, setFilters };
}
